<template>
  <v-card class="tax-summary-key-points px-4 pt-2 pb-4 my-0 elevation-0">
    <v-card-title class="px-1 pb-2">
      <h1 class="card-title">
        {{ $t(`views.tax.taxSummary.keyPoints.title`) }}
      </h1>
      <v-spacer />
      <ExportButton :click="downloadCsv" :loading="exportLoading" />
    </v-card-title>
    <v-card-text class="mt-0 pa-0">
      <BarLoading v-if="loading" />
      <template v-else>
        <v-simple-table dense fixed-header class="mt-4">
          <template v-slot:default>
            <tbody>
              <tr>
                <td>
                  <span>
                    {{ $t(`views.tax.taxSummary.keyPoints.totalProfits.title`) }}
                    <InfoIcon
                      :size="15"
                      :text="$t(`views.tax.taxSummary.keyPoints.totalProfits.info`)"
                    />
                  </span>
                </td>
                <td class="text-right input-small items-flex">
                  <currency
                    :value="yearSummary.totalProfits"
                    :currency="user.baseCurrency"
                    :text-green="yearSummary.totalProfits > 0"
                    :text-red="yearSummary.totalProfits < 0"
                    :hideDataNoPlanUser="hideDataNoPlanUser"
                  />
                  <div class="ml-1 items-flex">
                    (
                    <currency
                      :value="yearSummary.totalDestinationRetention"
                      :currency="user.baseCurrency"
                    />
                    )
                  </div>
                </td>
              </tr>
              <tr>
                <td>
                  <span>
                    {{ $t(`views.tax.taxSummary.keyPoints.dividends.title`) }}
                    <InfoIcon
                      :size="15"
                      :text="$t(`views.tax.taxSummary.keyPoints.dividends.info`)"
                    />
                  </span>
                </td>
                <td class="text-right input-small items-flex items-flex-end">
                  <currency
                    v-if="yearSummary.dividends"
                    :value="yearSummary.dividends.ingresosIntegros"
                    :currency="user.baseCurrency"
                    :hideDataNoPlanUser="hideDataNoPlanUser"
                  />
                  <span v-else></span>
                </td>
              </tr>
              <tr>
                <td>
                  <span>
                    {{ $t(`views.tax.taxSummary.keyPoints.totalInterestAndStake.title`) }}
                    <InfoIcon
                      :size="15"
                      :text="$t(`views.tax.taxSummary.keyPoints.totalInterestAndStake.info`)"
                    />
                  </span>
                </td>
                <td class="text-right input-small items-flex items-flex-end">
                  <currency
                    :value="yearSummary.totalInterestAndStake"
                    :currency="user.baseCurrency"
                  />
                  <div class="ml-1 items-flex">
                    (
                    <currency
                      :value="yearSummary.destinationRetentionInterest"
                      :currency="user.baseCurrency"
                    />
                    )
                  </div>
                </td>
              </tr>
              <tr>
                <td>
                  <span>
                    {{ $t(`views.tax.taxSummary.keyPoints.totalBonus.title`) }}
                    <InfoIcon
                      :size="15"
                      :text="$t(`views.tax.taxSummary.keyPoints.totalBonus.info`)"
                    />
                  </span>
                </td>
                <td class="text-right input-small items-flex items-flex-end">
                  <currency :value="yearSummary.totalBonus" :currency="user.baseCurrency" />
                  <div class="ml-1 items-flex">
                    (
                    <currency
                      :value="yearSummary.destinationRetentionBonus"
                      :currency="user.baseCurrency"
                    />
                    )
                  </div>
                </td>
              </tr>
              <tr>
                <td>
                  <span>
                    {{ $t(`views.tax.taxSummary.keyPoints.totalCommission.title`) }}
                    <InfoIcon
                      :size="15"
                      :text="$t(`views.tax.taxSummary.keyPoints.totalCommission.info`)"
                    />
                  </span>
                </td>
                <td class="text-right input-small items-flex items-flex-end">
                  <currency
                    :value="yearSummary.totalCommission"
                    :currency="user.baseCurrency"
                    :text-red="!!yearSummary.totalCommission"
                  />
                </td>
              </tr>
              <tr>
                <td>
                  <span>
                    {{ $t(`views.tax.taxSummary.keyPoints.totalPaidInterest.title`) }}
                    <InfoIcon
                      :size="15"
                      :text="$t(`views.tax.taxSummary.keyPoints.totalPaidInterest.info`)"
                    />
                  </span>
                </td>
                <td class="text-right input-small items-flex items-flex-end">
                  <currency
                    :value="yearSummary.totalPaidInterest"
                    :currency="user.baseCurrency"
                    :text-red="!!yearSummary.totalPaidInterest"
                  />
                </td>
              </tr>
              <tr>
                <td>
                  <span>
                    {{ $t(`views.tax.taxSummary.keyPoints.coupons.title`) }}
                    <InfoIcon
                      :size="15"
                      :text="$t(`views.tax.taxSummary.keyPoints.coupons.info`)"
                    />
                  </span>
                </td>
                <td class="text-right input-small items-flex items-flex-end">
                  <currency
                    v-if="yearSummary.coupons"
                    :value="yearSummary.coupons.totalBaseCurrency"
                    :currency="user.baseCurrency"
                  />
                  <span v-else></span>
                </td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>
      </template>
    </v-card-text>
  </v-card>
</template>

<script>
import { mapState } from 'vuex'
import { DateTime } from 'luxon'
import FileSaver from 'file-saver'
import { getTaxSummaryCsv } from '@/lib/api.js'
import Currency from '@/views/components/common/Currency'
import InfoIcon from '@/views/components/common/InfoIcon'
import BarLoading from '@/views/components/core/BarLoading.vue'
import ExportButton from '@/views/components/common/ExportButton'

export default {
  components: { Currency, InfoIcon, BarLoading, ExportButton },
  props: {
    startDate: {
      type: String,
      required: true,
    },
    endDate: {
      type: String,
      required: true,
    },
    yearSelected: {
      type: String,
      required: true,
    },
    yearSummary: {
      type: Object,
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    hideDataNoPlanUser: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      exportLoading: false,
    }
  },
  computed: {
    ...mapState(['user']),
  },
  methods: {
    async downloadCsv() {
      this.exportLoading = true

      const nowFormatted = DateTime.now().toFormat('yyyy_MM_dd HH_mm_ss')
      try {
        const result = await getTaxSummaryCsv({
          startDate: this.startDate,
          endDate: this.endDate,
          section: 'keyPoints',
        })
        const blob = new Blob([result.data], { type: 'text/csv;charset=utf-8' })
        const title = `${this.$t('document.title.tax')} ${this.yearSelected}. ${this.$t(
          `views.tax.taxSummary.keyPoints.title`
        )}`
        FileSaver.saveAs(blob, `Filios - ${title} - ${nowFormatted}.csv`)
      } catch (error) {
        this.$root.$snackbar.checkAndOpenCommonLoadError(error)
      }

      this.exportLoading = false
    },
  },
}
</script>

<style lang="scss">
.tax-summary-key-points {
  .items-flex {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: right;
  }
}
</style>
